import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const AdminPanel = ({ categories, onAddItem, onCancel, restaurantId, itemToEdit }) => {
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    description: '',
    category: categories[0] || 'Main Course',
    image_url: '',
    model_url: '',
    is_available: true,
    is_todays_special: false,
    dietary_badge: 'None',
    variants: [],
    related_items: []
  });

  const [allItems, setAllItems] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [modelFile, setModelFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isCustomCategory, setIsCustomCategory] = useState(false);

  // --- 3D LIBRARY STATE ---
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const [libraryModels, setLibraryModels] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [librarySearch, setLibrarySearch] = useState('');
  const [suggestedModels, setSuggestedModels] = useState([]);
  const [selectedLibraryModel, setSelectedLibraryModel] = useState(null); // Track which library model was chosen

  // --- FETCH LIBRARY MODELS ---
  const fetchLibrary = async () => {
    setLibraryLoading(true);
    try {
      const { data, error } = await supabase
        .from('model_library').select('*').order('name');
      if (error) throw error;
      setLibraryModels(data || []);
    } catch (err) {
      console.error('Error fetching model library:', err);
    } finally {
      setLibraryLoading(false);
    }
  };

  // --- INITIALIZE ---
  useEffect(() => {
    const fetchItems = async () => {
      const { data } = await supabase.from('menu_items').select('id, name, category').eq('restaurant_id', restaurantId);
      setAllItems(data || []);
    };
    fetchItems();

    if (itemToEdit) {
      setFormData({
        name: itemToEdit.name || '',
        price: itemToEdit.price || '',
        description: itemToEdit.description || '',
        category: itemToEdit.category || categories[0],
        image_url: itemToEdit.image_url || '',
        model_url: itemToEdit.model_url || '',
        is_available: itemToEdit.is_available ?? true,
        is_todays_special: itemToEdit.is_todays_special || false,
        dietary_badge: itemToEdit.dietary_badge || 'None',
        variants: itemToEdit.variants || [],
        related_items: itemToEdit.related_items || []
      });
      setImagePreview(itemToEdit.image_url || '');
    }
  }, [itemToEdit, categories, restaurantId]);

  // Fetch library models on mount for auto-suggestions
  useEffect(() => {
    fetchLibrary();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- AUTO-SUGGEST: When dish name changes, find matching models ---
  useEffect(() => {
    if (!formData.name || formData.name.length < 2 || libraryModels.length === 0) {
      setSuggestedModels([]);
      return;
    }

    const nameWords = formData.name.toLowerCase().split(/\s+/).filter(w => w.length > 2);
    if (nameWords.length === 0) { setSuggestedModels([]); return; }

    // Score each model based on how many words match name or tags
    const scored = libraryModels.map(model => {
      let score = 0;
      const modelNameLower = model.name.toLowerCase();
      const modelTags = (model.tags || []).map(t => t.toLowerCase());

      nameWords.forEach(word => {
        if (modelNameLower.includes(word)) score += 3; // Strong match on name
        if (modelTags.some(tag => tag.includes(word) || word.includes(tag))) score += 2; // Tag match
      });

      // Bonus for exact name containment
      if (modelNameLower.includes(formData.name.toLowerCase())) score += 5;

      return { ...model, score };
    });

    const matches = scored
      .filter(m => m.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);

    setSuggestedModels(matches);
  }, [formData.name, libraryModels]);

  // --- HANDLERS ---
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (type === 'image') {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    } else {
      setModelFile(file);
      setSelectedLibraryModel(null); // Clear library selection if uploading custom
    }
  };

  const handleSelectLibraryModel = (model) => {
    setFormData(prev => ({ ...prev, model_url: model.model_url }));
    setSelectedLibraryModel(model);
    setModelFile(null); // Clear custom upload
    setShowLibraryPicker(false);
  };

  const handleClearModel = () => {
    setFormData(prev => ({ ...prev, model_url: '' }));
    setSelectedLibraryModel(null);
    setModelFile(null);
  };

  const addVariant = () => setFormData(prev => ({ ...prev, variants: [...prev.variants, { name: '', price: '' }] }));
  const updateVariant = (index, field, value) => {
    const updated = [...formData.variants];
    updated[index][field] = value;
    setFormData(prev => ({ ...prev, variants: updated }));
  };
  const removeVariant = (index) => setFormData(prev => ({ ...prev, variants: prev.variants.filter((_, i) => i !== index) }));

  // --- UPSELL LOGIC ---
  const isSelected = (id) => {
    return (formData.related_items || []).map(String).includes(String(id));
  };

  const toggleRelatedItem = (id) => {
    const strId = String(id);
    setFormData(prev => {
      const current = (prev.related_items || []).map(String);
      if (current.includes(strId)) {
        return { ...prev, related_items: current.filter(itemId => itemId !== strId) };
      } else {
        return { ...prev, related_items: [...current, strId] };
      }
    });
  };

  // --- Helper: upload file with progress tracking ---
  const uploadWithProgress = (bucket, filePath, file) => {
    return new Promise((resolve, reject) => {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      const url = `${supabaseUrl}/storage/v1/object/${bucket}/${filePath}`;

      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Authorization', `Bearer ${supabaseKey}`);
      xhr.setRequestHeader('apikey', supabaseKey);
      xhr.setRequestHeader('x-upsert', 'true');

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          setUploadProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Upload failed: network error'));
      xhr.send(file);
    });
  };

  // --- SUBMIT ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUploadProgress(0);

    let imgUrl = formData.image_url;
    let modUrl = formData.model_url;

    try {
      if (imageFile) {
        const fileExt = imageFile.name.split('.').pop();
        const fileName = `${restaurantId}/${Date.now()}-img.${fileExt}`;
        const { error } = await supabase.storage.from('models').upload(fileName, imageFile);
        if (error) throw error;
        const { data } = supabase.storage.from('models').getPublicUrl(fileName);
        imgUrl = data.publicUrl;
      }

      if (modelFile) {
        setUploadProgress(10); // Start progress indication
        const fileExt = modelFile.name.split('.').pop();
        const fileName = `${restaurantId}/${Date.now()}-glb.${fileExt}`;
        await uploadWithProgress('models', fileName, modelFile);
        const { data } = supabase.storage.from('models').getPublicUrl(fileName);
        modUrl = data.publicUrl;
        setUploadProgress(100);
      }

      const itemData = {
        restaurant_id: restaurantId,
        name: formData.name,
        price: parseFloat(formData.price) || 0,
        description: formData.description,
        category: formData.category,
        image_url: imgUrl,
        model_url: modUrl,
        is_available: formData.is_available,
        is_todays_special: formData.is_todays_special,
        dietary_badge: formData.dietary_badge,
        variants: formData.variants,
        related_items: formData.related_items
      };

      let result;
      if (itemToEdit) {
        result = await supabase.from('menu_items').update(itemData).eq('id', itemToEdit.id).select().single();
        
        // --- STORAGE CLEANUP ---
        // If the update was successful, delete any orphaned files (old image or old model)
        if (!result.error) {
          const getStoragePath = (url) => {
            if (!url) return null;
            const match = url.split('?')[0].match(/\/storage\/v1\/object\/public\/models\/(.+)/);
            return match ? match[1] : null;
          };

          const pathsToDelete = [];
          
          // If image changed, delete old image
          if (itemToEdit.image_url && itemToEdit.image_url !== imgUrl) {
            const oldImagePath = getStoragePath(itemToEdit.image_url);
            if (oldImagePath) pathsToDelete.push(oldImagePath);
          }

          // If model changed, delete old model (but NEVER delete library models)
          if (itemToEdit.model_url && itemToEdit.model_url !== modUrl) {
            const oldModelPath = getStoragePath(itemToEdit.model_url);
            if (oldModelPath && !oldModelPath.startsWith('library/')) {
              pathsToDelete.push(oldModelPath);
            }
          }

          if (pathsToDelete.length > 0) {
            await supabase.storage.from('models').remove(pathsToDelete);
          }
        }
      } else {
        result = await supabase.from('menu_items').insert([itemData]).select().single();
      }

      if (result.error) throw result.error;
      onAddItem(result.data);

    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const selectedUpsells = allItems.filter(i => isSelected(i.id));

  // Library filter
  const filteredLibrary = libraryModels.filter(m => {
    const matchesSearch = librarySearch === '' ||
      m.name.toLowerCase().includes(librarySearch.toLowerCase()) ||
      (m.tags || []).some(t => t.includes(librarySearch.toLowerCase()));
    return matchesSearch;
  });


  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh] animate-fade-in">

        {/* HEADER */}
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-2xl">
          <h2 className="text-xl font-bold text-gray-900">{itemToEdit ? 'Edit Item' : 'Add New Dish'}</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-black font-bold text-xl">✕</button>
        </div>

        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto p-8">
          <form onSubmit={handleSubmit} className="space-y-6">

            {/* 1. AVAILABILITY TOGGLE */}
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="font-bold text-gray-900">Availability Status</h3>
                <p className="text-xs text-gray-500">Toggle off to mark as "Sold Out".</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" name="is_available" className="sr-only peer" checked={formData.is_available} onChange={handleInputChange} />
                <div className={`w-14 h-7 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all ${formData.is_available ? 'peer-checked:bg-green-500' : 'peer-checked:bg-gray-400'}`}></div>
              </label>
            </div>

            {/* BASIC DETAILS */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Name</label>
                <input required type="text" name="name" className="w-full p-3 border rounded-xl font-bold" placeholder="e.g. Butter Chicken" value={formData.name} onChange={handleInputChange} />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Base Price (₹)</label>
                <input required type="number" name="price" className="w-full p-3 border rounded-xl font-bold" placeholder="350" value={formData.price} onChange={handleInputChange} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Category</label>
                <div className="flex gap-2">
                  {isCustomCategory ? (
                    <input
                      autoFocus required type="text" name="category"
                      className="w-full p-3 border rounded-xl font-bold bg-white"
                      placeholder="New Category Name"
                      value={formData.category} onChange={handleInputChange}
                    />
                  ) : (
                    <select name="category" className="w-full p-3 border rounded-xl bg-white" value={formData.category} onChange={handleInputChange}>
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  )}
                  <button
                    type="button"
                    onClick={() => { setIsCustomCategory(!isCustomCategory); setFormData(prev => ({ ...prev, category: '' })); }}
                    className="p-3 bg-gray-100 rounded-xl hover:bg-gray-200 font-bold text-lg w-12 flex items-center justify-center transition-colors"
                  >
                    {isCustomCategory ? '✕' : '+'}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Dietary Badge</label>
                <select name="dietary_badge" className="w-full p-3 border rounded-xl bg-white" value={formData.dietary_badge} onChange={handleInputChange}>
                  <option value="None">None</option>
                  <option value="Veg">🟢 Veg</option>
                  <option value="Non-Veg">🔴 Non-Veg</option>
                  <option value="Vegan">🌿 Vegan</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Description</label>
              <textarea name="description" rows="2" className="w-full p-3 border rounded-xl text-sm" placeholder="Describe ingredients..." value={formData.description} onChange={handleInputChange}></textarea>
            </div>

            <div className="flex gap-4 items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" name="is_todays_special" checked={formData.is_todays_special} onChange={handleInputChange} className="w-5 h-5 accent-orange-500" />
                <span className="font-bold text-gray-700">🔥 Mark as Today's Special</span>
              </label>
            </div>

            <hr className="border-gray-100" />

            {/* UPSELLING SECTION */}
            <div className="bg-orange-50 p-6 rounded-2xl border border-orange-100">
              <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
                ⚡ Smart Upsell Recommendations
              </h3>
              <p className="text-xs text-gray-500 mb-4">Select items that go well with this dish (e.g., Fries, Drinks).</p>

              {selectedUpsells.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedUpsells.map(item => (
                    <span key={item.id} className="inline-flex items-center bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded-full border border-green-200">
                      {item.name}
                      <button type="button" onClick={() => toggleRelatedItem(item.id)} className="ml-2 text-green-600 hover:text-green-900 font-black">×</button>
                    </span>
                  ))}
                </div>
              )}

              <div className="max-h-40 overflow-y-auto grid grid-cols-2 gap-2 pr-1">
                {allItems.filter(i => i.id !== itemToEdit?.id).map(item => (
                  <label key={item.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${isSelected(item.id) ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                    <input type="checkbox" checked={isSelected(item.id)} onChange={() => toggleRelatedItem(item.id)} className="w-4 h-4 accent-green-500" />
                    <div className="truncate">
                      <span className="text-sm font-bold text-gray-800 block truncate">{item.name}</span>
                      <span className="text-[10px] text-gray-500 uppercase">{item.category}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <hr className="border-gray-100" />

            {/* VARIANTS */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-xs font-bold text-gray-500 uppercase">Portion Sizes / Variants</label>
                <button type="button" onClick={addVariant} className="text-blue-600 text-xs font-bold hover:underline">+ Add Variant</button>
              </div>
              {formData.variants.map((v, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input type="text" placeholder="Size" className="flex-1 p-2 border rounded-lg text-sm" value={v.name} onChange={(e) => updateVariant(i, 'name', e.target.value)} />
                  <input type="number" placeholder="Price" className="w-24 p-2 border rounded-lg text-sm" value={v.price} onChange={(e) => updateVariant(i, 'price', e.target.value)} />
                  <button type="button" onClick={() => removeVariant(i)} className="text-red-500 font-bold px-2">×</button>
                </div>
              ))}
            </div>

            {/* ================================ */}
            {/* MEDIA SECTION (ENHANCED WITH 3D LIBRARY) */}
            {/* ================================ */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-6">
                {/* Dish Image */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Dish Image</label>
                  <div className="relative h-32 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center overflow-hidden hover:bg-gray-100 transition-colors">
                    {imagePreview ? <img src={imagePreview} className="w-full h-full object-cover" alt="Preview" /> : <span className="text-gray-400 text-xs">Click to upload</span>}
                    <input type="file" accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => handleFileChange(e, 'image')} />
                  </div>
                </div>

                {/* 3D Model — Enhanced */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-2">3D Model</label>

                  {/* Current model status */}
                  {selectedLibraryModel ? (
                    // Library model selected
                    <div className="h-32 bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl flex flex-col items-center justify-center relative group">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">📦</span>
                        <span className="font-bold text-sm text-purple-700 truncate max-w-[140px]">{selectedLibraryModel.name}</span>
                      </div>
                      <span className="text-[10px] text-purple-500 font-bold uppercase">From Library</span>
                      <button
                        type="button"
                        onClick={handleClearModel}
                        className="absolute top-2 right-2 w-6 h-6 bg-white rounded-full text-gray-400 hover:text-red-500 flex items-center justify-center text-sm font-bold shadow-sm border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ) : modelFile ? (
                    // Custom file selected
                    <div className="h-32 bg-green-50 border-2 border-green-200 rounded-xl flex flex-col items-center justify-center relative group">
                      <span className="text-lg mb-1">📦</span>
                      <span className="text-xs font-bold text-green-700 truncate max-w-[160px]">{modelFile.name}</span>
                      <span className="text-[10px] text-green-500">({(modelFile.size / 1024 / 1024).toFixed(1)}MB)</span>
                      <button
                        type="button"
                        onClick={handleClearModel}
                        className="absolute top-2 right-2 w-6 h-6 bg-white rounded-full text-gray-400 hover:text-red-500 flex items-center justify-center text-sm font-bold shadow-sm border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ) : formData.model_url ? (
                    // Existing model URL (from edit)
                    <div className="h-32 bg-blue-50 border-2 border-blue-200 rounded-xl flex flex-col items-center justify-center relative group">
                      <span className="text-lg mb-1">✅</span>
                      <span className="text-xs font-bold text-blue-700">Model Uploaded</span>
                      <button
                        type="button"
                        onClick={handleClearModel}
                        className="absolute top-2 right-2 w-6 h-6 bg-white rounded-full text-gray-400 hover:text-red-500 flex items-center justify-center text-sm font-bold shadow-sm border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    // No model — show upload area
                    <div className="relative h-32 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center hover:bg-gray-100 transition-colors">
                      <span className="text-gray-400 text-xs">Click to upload .glb</span>
                      <input type="file" accept=".glb" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => handleFileChange(e, 'model')} />
                    </div>
                  )}
                </div>
              </div>

              {/* 3D LIBRARY: BROWSE BUTTON + SUGGESTIONS */}
              <div className="bg-gradient-to-r from-purple-50 via-indigo-50 to-blue-50 rounded-2xl border border-purple-100 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📦</span>
                    <span className="font-bold text-gray-800 text-sm">3D Model Library</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => { fetchLibrary(); setShowLibraryPicker(true); setLibrarySearch(''); }}
                    className="bg-white px-4 py-2 rounded-lg text-sm font-bold text-purple-700 border border-purple-200 hover:bg-purple-50 transition-colors shadow-sm"
                  >
                    Browse All →
                  </button>
                </div>

                {/* AUTO-SUGGESTIONS based on dish name */}
                {suggestedModels.length > 0 && (
                  <div>
                    <p className="text-[11px] text-gray-500 font-medium mb-2">✨ Suggested for "{formData.name}":</p>
                    <div className="flex gap-3 overflow-x-auto pb-1">
                      {suggestedModels.map(model => (
                        <button
                          key={model.id}
                          type="button"
                          onClick={() => handleSelectLibraryModel(model)}
                          className={`flex-shrink-0 flex items-center gap-3 bg-white p-2.5 pr-4 rounded-xl border transition-all hover:shadow-md active:scale-95 ${selectedLibraryModel?.id === model.id
                              ? 'border-purple-400 ring-2 ring-purple-100 shadow-md'
                              : 'border-gray-200 hover:border-purple-300'
                            }`}
                        >
                          <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                            {model.thumbnail_url ? (
                              <img src={model.thumbnail_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-xl text-gray-300">📦</div>
                            )}
                          </div>
                          <div className="text-left">
                            <span className="font-bold text-gray-800 text-sm block truncate max-w-[120px]">{model.name}</span>
                            <span className="text-[10px] text-gray-400 font-medium">{(model.tags || []).slice(0, 2).join(', ')}</span>
                          </div>
                          {selectedLibraryModel?.id === model.id && (
                            <span className="text-purple-600 font-bold text-sm ml-1">✓</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {suggestedModels.length === 0 && formData.name.length > 2 && libraryModels.length > 0 && (
                  <p className="text-[11px] text-gray-400 italic">No matching models found for "{formData.name}". Try browsing the full library.</p>
                )}

                {suggestedModels.length === 0 && libraryModels.length === 0 && formData.name.length > 2 && (
                  <p className="text-[11px] text-gray-400 italic">Model library is empty.</p>
                )}
              </div>
            </div>

            {/* FOOTER */}
            <div className="pt-4 flex gap-4">
              <button type="button" onClick={onCancel} className="flex-1 py-3 font-bold text-gray-500 hover:bg-gray-100 rounded-xl">Cancel</button>
              <button type="submit" disabled={loading} className="flex-1 py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-black shadow-lg disabled:opacity-50 transition-all relative overflow-hidden">
                {loading && uploadProgress < 100 && (
                  <div className="absolute inset-0 bg-green-500/20" style={{ width: `${uploadProgress}%`, transition: 'width 0.3s ease' }} />
                )}
                <span className="relative z-10">
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                       <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                       {uploadProgress < 100 ? `Uploading ${uploadProgress}%` : 'Saving...'}
                    </span>
                  ) : itemToEdit ? 'Save Changes' : 'Save Item'}
                </span>
              </button>
            </div>

          </form>
        </div>
      </div>

      {/* ================================ */}
      {/* LIBRARY PICKER MODAL            */}
      {/* ================================ */}
      {showLibraryPicker && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4 animate-fade-in" onClick={() => setShowLibraryPicker(false)}>
          <div className="bg-white w-full max-w-3xl max-h-[85vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>

            {/* Picker Header */}
            <div className="p-5 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">📦 Choose a 3D Model</h3>
                <p className="text-xs text-gray-500 mt-0.5">{libraryModels.length} model{libraryModels.length !== 1 ? 's' : ''} available</p>
              </div>
              <button onClick={() => setShowLibraryPicker(false)} className="text-gray-400 hover:text-black font-bold text-xl">✕</button>
            </div>

            {/* Search & Filter */}
            <div className="p-4 border-b border-gray-100 flex flex-col md:flex-row gap-3">
              <div className="flex-1 relative">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  autoFocus
                  placeholder="Search models by name or tag..."
                  value={librarySearch}
                  onChange={e => setLibrarySearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg outline-none focus:border-gray-400 transition-colors text-sm font-medium"
                />
              </div>
            </div>

            {/* Model Grid */}
            <div className="flex-1 overflow-y-auto p-4">
              {libraryLoading ? (
                <div className="py-12 text-center text-gray-400 font-bold">Loading models...</div>
              ) : filteredLibrary.length === 0 ? (
                <div className="py-12 text-center text-gray-400">
                  <div className="text-4xl mb-3">🔍</div>
                  <p className="font-bold">No models found</p>
                  <p className="text-sm mt-1">Try a different search term.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filteredLibrary.map(model => (
                    <button
                      key={model.id}
                      type="button"
                      onClick={() => handleSelectLibraryModel(model)}
                      className={`text-left rounded-xl border overflow-hidden transition-all hover:shadow-lg active:scale-[0.98] group ${selectedLibraryModel?.id === model.id
                          ? 'border-purple-400 ring-2 ring-purple-100 shadow-lg'
                          : 'border-gray-200 hover:border-purple-200'
                        }`}
                    >
                      {/* Thumbnail */}
                      <div className="aspect-square bg-gray-100 relative overflow-hidden">
                        {model.thumbnail_url ? (
                          <img src={model.thumbnail_url} alt={model.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-4xl text-gray-300">📦</div>
                        )}
                        {selectedLibraryModel?.id === model.id && (
                          <div className="absolute inset-0 bg-purple-600/20 flex items-center justify-center">
                            <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg">✓</div>
                          </div>
                        )}

                      </div>
                      {/* Info */}
                      <div className="p-3">
                        <h4 className="font-bold text-gray-800 text-sm truncate">{model.name}</h4>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(model.tags || []).slice(0, 3).map((tag, i) => (
                            <span key={i} className="bg-gray-100 text-gray-500 text-[9px] font-bold px-1.5 py-0.5 rounded">{tag}</span>
                          ))}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-between items-center">
              <button type="button" onClick={() => setShowLibraryPicker(false)} className="text-gray-500 font-bold text-sm hover:text-gray-900 transition-colors">Cancel</button>
              {selectedLibraryModel && (
                <span className="text-sm text-purple-700 font-bold flex items-center gap-2">
                  ✓ Selected: {selectedLibraryModel.name}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;